<div class="loading-spinner" *ngIf="isLoading; else loaded">
  <div class="cc-icon cc-icon-spinner"></div>
</div>
<ng-template #loaded>
  <form #form="ngForm" novalidate (submit)="form.valid && onSubmit()" class="sm-flex sm-flex-col sm-gap-4">
    <h1 class="sm-text-xl sm-font-medium" i18n>Dashpivot Billing</h1>

    <p i18n>These billing settings apply to your Dashpivot subscription only.</p>

    <!-- Trial End Date Selector -->
    <div class="row sm-items-center">
      <label i18n for="trial-end-date-selector" class="default-label col-form-label col-sm-4"
        >Dashpivot trial end date</label
      >
      <div class="col-sm-8 input-group date-selector-container">
        <input
          #datePicker="ngbDatepicker"
          [class.disabled]="!isSuperAdmin"
          class="input-text-base date-picker"
          [readOnly]="!isSuperAdmin"
          placeholder="dd-mm-yyyy"
          name="trial-end-date-selector"
          ngbDatepicker
          container="body"
          [(ngModel)]="formModel.dashpivotTrialEndDate"
        />
        <div class="input-group-append">
          <button
            type="button"
            class="btn date-picker-btn input-text-base"
            data-test="trial-end-date"
            [class.disabled]="!isSuperAdmin"
            (click)="canDisplayDatePicker($event) && datePicker.toggle()"
          >
            <i class="cc-icon cc-icon-calendar"></i>
          </button>
        </div>
      </div>
    </div>
    <!-- Plan type -->
    <div class="row sm-max-h-8">
      <label i18n for="planType" class="default-label col-form-label col-sm-4">Dashpivot plan type</label>
      <div class="col-sm-8">
        <ng-select
          [readonly]="!isSuperAdmin"
          [items]="planTypeSelectorValues"
          bindLabel="label"
          bindValue="value"
          [clearable]="false"
          [searchable]="false"
          [(ngModel)]="formModel.dashpivotPlanType"
          [class.disabled]="!isSuperAdmin"
          class="dropdown-selector sm-w-full"
          name="planType"
          data-test="planType"
        >
        </ng-select>

        <i
          *ngIf="formModel.dashpivotPlanType === companyPlanTypes.Legacy"
          class="cc-icon cc-icon-warning legacy-plan-tooltip"
          [ngbPopover]="legacyTooltip"
          popoverClass="label-popover"
          placement="right"
          triggers="manual"
          container="body"
          data-test="legacy-plan-tooltip-icon"
          (click)="p.open()"
          #p="ngbPopover"
        ></i>

        <ng-template #legacyTooltip>
          <div data-test="legacy-plan-tooltip-box">
            <p i18n>
              It appears this workspace is on a legacy plan, and may not have access to the latest features.
              Please contact our team to discuss transitioning to one of our current plans.
            </p>
            <button
              type="button"
              data-test="legacy-plan-tooltip-contact-button"
              class="btn button-regular sm-mt-2 sm-bg-default-blue sm-text-white hover:sm-bg-default-dark-blue"
              (click)="openLiveChat()"
            >
              <i class="cc-icon cc-icon-chat" aria-hidden="true"></i>
              <span i18n>Contact us</span>
            </button>
          </div>
        </ng-template>
      </div>
    </div>

    <!-- Subscription status -->
    <div class="row">
      <label i18n for="subscriptionStatus" class="default-label col-form-label col-sm-4"
        >Dashpivot subscription status</label
      >
      <div class="col-sm-8">
        <ng-select
          [readonly]="!isSuperAdmin"
          [items]="subscriptionStatusSelectorValues"
          bindLabel="label"
          bindValue="value"
          [clearable]="false"
          [searchable]="false"
          [(ngModel)]="formModel.dashpivotSubscriptionStatus"
          [class.disabled]="!isSuperAdmin"
          class="dropdown-selector sm-w-full"
          name="subscriptionStatus"
          id="subscriptionStatus"
          placeholder="Select..."
        >
        </ng-select>
      </div>
    </div>

    <!-- Initial Subscription Date -->
    <div class="row sm-items-center" data-test="initialSubscriptionDate">
      <label i18n for="initialSubscriptionDate" class="default-label col-form-label col-sm-4"
        >Dashpivot initial subscription date</label
      >
      <div class="col-sm-8 input-group date-selector-container">
        <input
          #subscriptionDatePicker="ngbDatepicker"
          [class.disabled]="!isSuperAdmin"
          class="input-text-base date-picker"
          [readOnly]="!isSuperAdmin"
          placeholder="dd-mm-yyyy"
          name="initialSubscriptionDate"
          ngbDatepicker
          container="body"
          [(ngModel)]="formModel.dashpivotInitialSubscriptionDate"
        />
        <div>
          <button
            type="button"
            class="btn date-picker-btn input-text-base"
            [class.disabled]="!isSuperAdmin"
            data-test="initial-subscription-date-selector"
            (click)="canDisplayDatePicker($event) && subscriptionDatePicker.toggle()"
          >
            <i class="cc-icon cc-icon-calendar"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Subscription Term -->
    <div class="row">
      <label i18n for="subscriptionTerm" class="default-label col-form-label col-sm-4"
        >Dashpivot subscription term</label
      >
      <div class="col-sm-8">
        <ng-select
          [readonly]="!isSuperAdmin"
          [items]="subscriptionTermSelectorValues"
          bindLabel="label"
          bindValue="value"
          [clearable]="false"
          [searchable]="false"
          [(ngModel)]="formModel.dashpivotSubscriptionTerm"
          [class.disabled]="!isSuperAdmin"
          class="dropdown-selector sm-w-full"
          name="subscriptionTerm"
          id="subscriptionTerm"
          placeholder="Select..."
        >
        </ng-select>
      </div>
    </div>

    <!-- Billing Cycle -->
    <div class="row">
      <label i18n for="billingCycle" class="default-label col-form-label col-sm-4"
        >Dashpivot billing cycle</label
      >
      <div class="col-sm-8">
        <ng-select
          [readonly]="!isSuperAdmin"
          [items]="billingCycleSelectorValues"
          bindLabel="label"
          bindValue="value"
          [clearable]="false"
          [searchable]="false"
          [(ngModel)]="formModel.dashpivotBillingCycle"
          [class.disabled]="!isSuperAdmin"
          class="dropdown-selector sm-w-full"
          name="billingCycle"
          id="billingCycle"
          placeholder="Select..."
        >
        </ng-select>
      </div>
    </div>

    <!-- Group Billing -->
    <div class="row" data-test="group-billing-input-toggle">
      <label i18n class="default-label col-form-label col-sm-4">Group billing</label>
      <div class="col-sm-8">
        <cc-input-toggle
          class="sm-flex sm-items-center sm-gap-x-2"
          [popoverContent]="groupBillingTooltip"
          [(modelValue)]="formModel.groupBilling"
          [isDisabled]="!isSuperAdmin"
          (onUpdate)="groupBillingToggleClicked($event)"
        >
        </cc-input-toggle>
      </div>
    </div>

    <!-- Legacy Restrictions -->
    <div
      class="row"
      *ngIf="formModel.dashpivotPlanType === companyPlanTypes.Legacy"
      data-test="legacy-restrictions-input-toggle"
    >
      <label i18n class="default-label col-form-label col-sm-4">Dashpivot legacy plan restrictions</label>
      <div class="col-sm-8">
        <cc-input-toggle
          class="sm-flex sm-items-center sm-gap-x-2"
          [popoverContent]="legacyRestrictionsTooltip"
          [(modelValue)]="formModel.dashpivotLegacyPlanRestrictionsEnabled"
          [isDisabled]="!isSuperAdmin"
          (onUpdate)="legacyRestrictionToggleClicked()"
        >
        </cc-input-toggle>
      </div>
    </div>

    <cc-company-billing-licenses
      [model]="formModel"
      [isSuperAdmin]="isSuperAdmin"
      [pricingInputs]="pricingInputs"
    ></cc-company-billing-licenses>

    <!-- Save -->
    <div class="sm-mb-8 sm-text-left">
      <button
        type="submit"
        data-test="save-button"
        class="sm-p-2 button-green sm-mb-6 sm-rounded button-green"
        [class.disabled]="!canSaveSettings || isSaving"
        (click)="onSubmit($event)"
      >
        Save changes
      </button>
    </div>
  </form>
</ng-template>
